import React, { useEffect, useState } from 'react';
import { useStockTransfersStore } from '../../../../store/stockTransfersStore';
import { useSkusStore } from '../../../../store/skusStore';
import styles from './CreateStockTransferModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface LineItem {
  sku_id: string;
  quantity: string;
}

const emptyLine = (): LineItem => ({ sku_id: '', quantity: '1' });

export const CreateStockTransferModal: React.FC<Props> = ({ open, onClose }) => {
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const create = useStockTransfersStore((s) => s.create);
  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);

  useEffect(() => {
    if (!open) {
      setSource(''); setDestination(''); setNotes('');
      setLines([emptyLine()]); setError(null); setSubmitting(false);
      return;
    }
    if (skus.length === 0) fetchSkus().catch(() => undefined);
  }, [open, skus.length, fetchSkus]);

  if (!open) return null;

  const updateLine = (idx: number, patch: Partial<LineItem>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!source.trim() || !destination.trim()) {
      setError('Source and destination locations are required');
      return;
    }
    if (source.trim().toLowerCase() === destination.trim().toLowerCase()) {
      setError('Source and destination must be different');
      return;
    }
    const valid = lines.filter((l) => l.sku_id && parseInt(l.quantity, 10) > 0);
    if (valid.length === 0) {
      setError('Add at least one line item with a positive quantity');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await create({
        source_location: source.trim(),
        destination_location: destination.trim(),
        notes: notes.trim() || undefined,
        items: valid.map((l) => ({ sku_id: Number(l.sku_id), quantity: parseInt(l.quantity, 10) })),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>New Stock Transfer</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Source Location <span className={styles.required}>*</span></span>
              <input type="text" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. WH-A" required />
            </label>
            <label className={styles.field}>
              <span>Destination Location <span className={styles.required}>*</span></span>
              <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g. WH-B" required />
            </label>
          </div>

          <label className={styles.field}>
            <span>Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} />
          </label>

          <div className={styles.linesHeader}>
            <span className={styles.linesTitle}>Items <span className={styles.required}>*</span></span>
            <button type="button" className={styles.addLineBtn} onClick={() => setLines((p) => [...p, emptyLine()])}>+ Add Line</button>
          </div>

          <div className={styles.lines}>
            {lines.map((line, idx) => (
              <div key={idx} className={styles.lineRow}>
                <select value={line.sku_id} onChange={(e) => updateLine(idx, { sku_id: e.target.value })} required>
                  <option value="">— Select product —</option>
                  {skus.map((s) => (
                    <option key={s.id} value={s.id}>{s.sku_code} — {s.product_name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  value={line.quantity}
                  onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className={styles.removeLineBtn}
                  onClick={() => setLines((p) => p.filter((_, i) => i !== idx))}
                  disabled={lines.length === 1}
                >×</button>
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateStockTransferModal;
